import { User } from 'types';
import { userSettingsService } from 'core/API/userSettings';

/**
 * Utility functions for user settings operations
 */
export const userSettingsUtils = {
	/**
	 * Format user display name
	 */
	formatUserName: (user: User): string => {
		const parts = [user.firstName, user.middleName, user.lastName].filter(Boolean);
		return parts.join(' ');
	},

	/**
	 * Format user status for display
	 */
	formatUserStatus: (active: boolean): string => {
		return active ? 'Active' : 'Inactive';
	},

	/**
	 * Check if user is separated
	 */
	isUserSeparated: (user: User): boolean => {
		return user.separation === true;
	},

	/**
	 * Get user hierarchy display
	 */
	formatHierarchy: (hierarchy: number): string => {
		// Define hierarchy levels based on your business logic
		const hierarchyMap: Record<number, string> = {
			0: 'Executive',
			1: 'Manager',
			2: 'Team Lead',
			3: 'Employee',
			4: 'Intern',
		};
		return hierarchyMap[hierarchy] || `Level ${hierarchy}`;
	},

	/**
	 * Validate email format
	 */
	isValidEmail: (email: string): boolean => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	},

	/**
	 * Validate phone number format
	 */
	isValidPhoneNumber: (phone: string): boolean => {
		// Basic phone validation - adjust based on your requirements
		const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
		return phoneRegex.test(phone.replace(/[\s\-()]/g, ''));
	},

	/**
	 * Generate user initials for avatar
	 */
	getUserInitials: (user: User): string => {
		const firstInitial = user.firstName?.charAt(0)?.toUpperCase() || '';
		const lastInitial = user.lastName?.charAt(0)?.toUpperCase() || '';
		return `${firstInitial}${lastInitial}`;
	},

	/**
	 * Sort users by various criteria
	 */
	sortUsers: (users: User[], sortBy: 'name' | 'email' | 'hierarchy' | 'active', order: 'asc' | 'desc' = 'asc'): User[] => {
		return [...users].sort((a, b) => {
			let aValue: any;
			let bValue: any;

			switch (sortBy) {
				case 'name':
					aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
					bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
					break;
				case 'email':
					aValue = a.email.toLowerCase();
					bValue = b.email.toLowerCase();
					break;
				case 'hierarchy':
					aValue = a.hierarchy;
					bValue = b.hierarchy;
					break;
				case 'active':
					aValue = a.userType?.active ? 1 : 0;
					bValue = b.userType?.active ? 1 : 0;
					break;
				default:
					return 0;
			}

			if (aValue < bValue) return order === 'asc' ? -1 : 1;
			if (aValue > bValue) return order === 'asc' ? 1 : -1;
			return 0;
		});
	},

	/**
	 * Clean hierarchy display name: remove parenthetical content and standalone 'node'
	 */
	cleanHierarchyName: (raw?: string): string => {
		if (!raw) return '';
		let s = raw.replace(/\s*\([^)]*\)\s*/g, ' ');
		s = s.replace(/\bnode\b/gi, ' ');
		return s.replace(/\s+/g, ' ').trim();
	},

	/**
	 * Filter users by search term across all list-relevant fields
	 */
	filterUsers: (users: User[], searchTerm: string): User[] => {
		if (!searchTerm.trim()) return users;

		const lowercaseSearch = searchTerm.toLowerCase().trim();
		return users.filter((user) => {
			const firstName = (user?.firstName || '').toLowerCase();
			const lastName = (user?.lastName || '').toLowerCase();
			const middleName = (user?.middleName || '').toLowerCase();
			const email = (user?.email || '').toLowerCase();
			const phone = (user?.phone || '').toLowerCase();
			const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ');
			const hierarchyName = userSettingsUtils.cleanHierarchyName(user?.hierarchy?.name).toLowerCase();
			const roleName = (user?.securityRole?.name || '').toLowerCase();

			return (
				firstName.includes(lowercaseSearch) ||
				lastName.includes(lowercaseSearch) ||
				middleName.includes(lowercaseSearch) ||
				email.includes(lowercaseSearch) ||
				phone.includes(lowercaseSearch) ||
				fullName.includes(lowercaseSearch) ||
				hierarchyName.includes(lowercaseSearch) ||
				roleName.includes(lowercaseSearch)
			);
		});
	},

	/**
	 * Toggle user active status
	 */
	toggleUserStatus: async (user: User): Promise<User | null> => {
		try {
			return await userSettingsService.toggleUserStatus(user.id, !user.userType?.active);
		} catch (error) {
			console.error('Failed to toggle user status:', error);
			return null;
		}
	},

	/**
	 * Calculate user age from start date
	 */
	calculateTenure: (startDate: any): number => {
		if (!startDate) return 0;
		const start = typeof startDate === 'string' ? new Date(startDate) : startDate.toDate();
		const now = new Date();
		const diffTime = Math.abs(now.getTime() - start.getTime());
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return Math.floor(diffDays / 365);
	},
};

export default userSettingsUtils;
